import type { Translations, Language } from './types';

export type { Language };

export const translations: Translations = {
  he: {
    nav: {
      services: "שירותים",
      articles: "מאמרים",
      caseStudies: "סיפורי הצלחה",
      resources: "משאבים",
      process: "תהליך עבודה",
      about: "עלינו",
      contact: "בואו נדבר",
      langToggle: "English"
    },
    hero: {
      badge: "AI אמיתי. מערכות אמיתיות.",
      headline: "מטמיעים AI בחברה שלכם",
      subheadline: "החלק הקשה ב-AI הוא לא ה-AI — אלא לחבר אותו למערכות שאתם באמת עובדים איתן. בדיוק בזה אנחנו מתמחים כבר 20 שנה. אז כשאנחנו בונים לכם סוכני AI — הם עובדים, ועומדים במבחן המציאות.",
      ctaPrimary: "בואו נדבר",
      ctaSecondary: "לשירותים שלנו",
      stats: {
        projects: "פרויקטים שנשלחו",
        experience: "שנות פיתוח תוכנה",
        success: "אחוזי הצלחה"
      },
      scrollDown: "גללו למטה",
      scrollDownAria: "גלול למטה",
      systems: [
        {
          caption: "Luxhype Fashion · מיפוי מידות עם AI, באישור אנושי",
          alt: "הצעות מיפוי AI המשוות בין שתי הצעות מודל עם ציוני ביטחון, ממתינות לאישור אנושי"
        },
        {
          caption: "PersonaAI · שיחות לקוחות בזמן אמת",
          alt: "סוכן AI מנהל שיחת לקוח בזמן אמת בתיבת דואר משותפת"
        },
        {
          caption: "PriorityHub · תיקון כתובות אוטונומי",
          alt: "יומן שינויים של כתובות משלוח שתוקנו על ידי AI עם ציוני ביטחון"
        }
      ],
      systemsAria: "בחירת מערכת להצגה"
    },
    problems: {
      badge: "למה לקוחות מגיעים אלינו",
      title: "נשמע מוכר?",
      subtitle: "אם אתם מרגישים ככה — אתם לא לבד. אלה בדיוק הסיבות שלקוחות פונים אלינו.",
      problems: [
        {
          title: "ניסינו הכל. שום דבר לא עובד.",
          desc: "קניתם פלאגינים, שכרתם פרילנסרים, ניסיתם לחבר מערכות בעצמכם. עדיין שבור, ובזבזתם חודשים."
        },
        {
          title: "אנחנו טובעים בעבודה ידנית",
          desc: "הצוות מבלה שעות כל יום בהעתקה-הדבקה, תיקון שגיאות בהזמנות וסנכרון נתונים בין מערכות."
        },
        {
          title: "אין פתרון מוכן למה שאנחנו צריכים",
          desc: "חיפשתם. עשיתם דמואים. שום דבר לא מתאים. אתם צריכים משהו שנבנה במיוחד לעסק שלכם."
        },
        {
          title: "כבר שרפנו הון — ולא קיבלנו כלום",
          desc: "מפתחים קודמים לקחו נצח, גבו יותר מדי, והשאירו אתכם עם קוד חצי-עובד. אתם צריכים מישהו שמספק."
        },
        {
          title: "אנחנו לא יודעים מאיפה להתחיל",
          desc: "הטכנולוגיה מבלבלת. האפשרויות מציפות. אתם צריכים מדריך, לא רק מפתח."
        },
        {
          title: "כולם איטיים ויקרים",
          desc: "חברות אחרות הציעו חודשים של עבודה ותקציבים מטורפים. אתם צריכים שותף שזז מהר בלי לקצר פינות."
        }
      ],
      cta: "קבלו עזרה מקצועית"
    },
    services: {
      title: "השירותים שלנו",
      subtitle: "אנחנו בונים את מה שמוצרי מדף לא יכולים — סוכני AI, תוכנה מותאמת, והאינטגרציות שמחברות את הכל למערכות האמיתיות שלכם.",
      crossCutting: "AI נמצא בכל מה שאנחנו עושים — ככלי שאנחנו מפתחים איתו, כיכולת שאנחנו מטמיעים כשצריך, וכמוצר שאנחנו בונים מאפס.",
      aiAgents: {
        title: "סוכני AI",
        desc: "אנחנו בונים סוכני AI ועוזרים חכמים שעושים עבודה אמיתית — מנהלים שיחות עם לקוחות, מעבדים מסמכים ופועלים בתוך המערכות החיות שלכם.",
        cta: "למידע נוסף",
        benefits: [
          "סוכני AI ועוזרים מותאמים",
          "עיבוד מסמכים ונתונים",
          "AI בתוך המערכות החיות",
          "בנוי להישאר אמין"
        ]
      },
      custom: {
        title: "פיתוח תוכנה בהתאמה אישית",
        desc: "כשאין מוצר מדף שמתאים, אנחנו בונים אותו — פלטפורמות, מרקטפלייסים, אפליקציות ומערכות מורכבות, עם AI מוטמע בדיוק במקומות שבהם הוא עושה את ההבדל.",
        cta: "למידע נוסף",
        benefits: [
          "פלטפורמות B2B ומרקטפלייסים",
          "אפליקציות מובייל (iOS ו-Android)",
          "מערכות מותאמות מורכבות",
          "AI מוטמע במקום שעוזר"
        ]
      },
      integration: {
        title: "אינטגרציה בין מערכות",
        desc: "אנחנו מחברים בין המערכות שלכם — ERP (Priority, SAP), מסחר ועוד — עם סנכרון בזמן אמת ותהליכים אוטומטיים שמספקים ל-AI נתונים נקיים ואמינים לעבוד איתם.",
        cta: "למידע נוסף",
        benefits: [
          "חיבור ERP למערכות מסחר",
          "סנכרון נתונים בזמן אמת",
          "תהליכים אוטומטיים",
          "נתונים נקיים שה-AI יכול לנצל"
        ]
      },
      benefitsTitle: "מה אנחנו מציעים:",
      badge: "השירותים שלנו",
      learnMore: "למידע נוסף",
      notSure: "לא בטוחים מה מתאים לכם?",
      talkToUs: "דברו איתנו"
    },
    about: {
      title: "הסיפור של Ronnie Projects",
      subtitle: "אנחנו בונים את מה שפתרונות מוכנים לא יכולים",
      storyTitle: "הסיפור שלנו",
      story: [
        "Ronnie Projects מתמחה בפתרון בעיות טכנולוגיות מורכבות — כאלה שמוצרי מדף לא פותרים. אנחנו בונים אינטגרציות בין מערכות, פלטפורמות B2B מותאמות, פתרונות אוטומציה חכמים ומערכות מידע ייחודיות.",
        "מייסד בית התוכנה, מיכאל אופנגנדן, בעל ניסיון של למעלה משני עשורים בפיתוח תוכנה. לאורך הקריירה שלו שימש בתפקידי ניהול בכירים בחברות מובילות, ניהל צוותי פיתוח מתקדמים בסטארטאפים וחברות טכנולוגיה, וצבר ידע עמוק באפיון ופיתוח מערכות מתקדמות."
      ],
      teamTitle: "הצוות שלנו",
      team: "צוות Ronnie Projects מורכב ממומחים מהשורה הראשונה, כולל מנהלי פרויקטים, מנתחי מערכות, מפתחי Backend ו-Frontend, מפתחי אפליקציות ואנשי QA. בעת הצורך, אנו משלבים יועצים ומפתחים נוספים כדי לספק את הפתרונות הטובים ביותר לכל פרויקט.",
      serviceTitle: "מה אנחנו עושים",
      service: "אנחנו מתמחים בשלושה תחומים: סוכני AI (סוכנים ועוזרים חכמים שמחוברים למערכות החיות שלכם), פיתוח תוכנה בהתאמה אישית (פלטפורמות B2B, מרקטפלייסים, אפליקציות ומערכות מורכבות), ואינטגרציה בין מערכות (ERP למסחר, תהליכים אוטומטיים ונתונים נקיים שה-AI יכול לעבוד איתם). כל פרויקט נבנה בהתאמה מלאה לצרכים הייחודיים שלכם.",
      valuesTitle: "הערכים שלנו",
      valuesDesc: "אנחנו מאמינים שכל בעיה טכנולוגית ניתנת לפתרון. הגישה שלנו מתחילה בהבנה עמוקה של הצורך העסקי, ממשיכה באפיון מדויק, ומסתיימת בפתרון שעובד בדיוק כמו שצריך.",
      values: [
        { title: "פתרון בעיות מורכבות", desc: "כשמוצרי מדף לא עובדים ופלאגינים נשברים — אנחנו בונים מה שבאמת עובד." },
        { title: "התאמה מלאה", desc: "אנחנו לא מאמינים בפתרונות גנריים. כל פרויקט נבנה בדיוק לפי הצרכים שלכם." },
        { title: "שותפות לטווח ארוך", desc: "אנחנו לא רק ספק תוכנה, אנחנו שותף אסטרטגי שמלווה אתכם בכל שלב." }
      ],
      cta: "קבלו הצעת מחיר",
      badge: "הכירו אותנו",
      whoWeAre: "מי אנחנו",
      teamImageAlt: "צוות העבודה שלנו",
      yearsExperience: "שנות ניסיון",
      techSolutionsAlt: "פתרונות טכנולוגיים",
      whatWeDo: "מה אנחנו עושים",
      ourPrinciples: "העקרונות שלנו",
      bookConsultation: "בואו נדבר",
      ctaTitle: "בואו נעשה את זה יחד",
      ctaDesc: "מוכנים להתחיל? צרו איתנו קשר היום ונדבר על איך נוכל לעזור לכם",
      stats: {
        yearsExp: "שנות ניסיון",
        projects: "פרויקטים שהושלמו",
        clients: "לקוחות מרוצים",
        success: "אחוזי הצלחה"
      }
    },
    serviceDetail: {
      'ai-agents': {
        title: "סוכני AI ועוזרים חכמים",
        subtitle: "סוכני AI מותאמים שעושים עבודה אמיתית בתוך המערכות החיות שלכם — לא עוד דמו, אלא סוכן שבאמת פועל.",
        paragraphs: [
          "כולם מדברים על AI, אבל רוב הפתרונות נתקעים בהדגמה — הם לא יודעים לגעת במערכות האמיתיות של העסק, ולכן לא באמת עוזרים. אנחנו בונים סוכני AI שמחוברים למערכות שלכם ועושים עבודה אמיתית, יום אחרי יום: מנהלים שיחות עם לקוחות, עוזרים לצוות הפנימי, והופכים נתונים גולמיים להחלטות אוטומטיות בתוך המערכות שלכם.",
          "והתוצאות מדברות בעד עצמן. עבור חברת תעופה בנינו מנוע AI שמחובר ישירות ל-Amadeus ומטפל בבקשות החזר אוטומטית — תהליך שלקח 15 עד 30 דקות לבקשה הפך לעניין של שניות, עם כמעט אפס טעויות. סוכן שירות לקוחות אחר שבנינו קיצר את זמן הטיפול הממוצע מ-12 דקות לפחות מ-90 שניות. ואת PersonaAI — פלטפורמת סוכני AI שמנהלת מכירות, שירות ותמיכה בשישה ערוצים — בנינו מהיסוד.",
          "ההבדל בין סקריפט אוטומציה שביר לסוכן AI אמיתי הוא שהסוכן מסיק, מסתגל ומחליט: כשהתנאים משתנים הוא מתאים את עצמו, וכשחסר מידע הוא יודע להשלים. אבל החלק הקשה הוא לא ה-AI עצמו — אלא לחבר אותו ל-ERP, למערכות המסחר ולנתונים החיים שלכם בצורה בטוחה ואמינה. בדיוק בזה אנחנו מתמחים כבר 20 שנה.",
          "אנחנו בונים לפרודקשן מהיום הראשון: מנגנון שמונע מהסוכן \"להמציא\" תשובות, אדם בלולאה על החלטות רגישות, גדרות בטיחות ואימות בעלות לפני כל פעולה משמעותית, וניטור מלא של מה שהסוכן עושה. אנחנו גם לא נועלים אתכם לספק אחד — בוחרים את מודל ה-AI הנכון לכל משימה."
        ],
        features: [
          { title: "סוכנים מול לקוחות", desc: "סוכני AI שמנהלים שיחות, עונים על שאלות ומטפלים בפניות — ומעבירים לנציג אנושי בדיוק כשצריך." },
          { title: "עוזרים פנימיים לצוות", desc: "עוזרי AI ששולפים תשובות מהידע והמערכות הפנימיות שלכם, מנסחים תגובות ומשלימים משימות תפעוליות — כדי שהצוות יפסיק לחפש ויתחיל להחליט." },
          { title: "ממידע להחלטות", desc: "סוכנים שלא רק קוראים את הנתונים — הם מסיקים מהם: מיישמים את חוקי העסק שלכם, מזהים חריגות ומחזירים את ההחלטה הנכונה אל תוך המערכות." },
          { title: "מחובר למערכות החיות שלכם", desc: "חיבור הסוכנים ל-ERP, למסחר ולנתונים — כך שהם פועלים על מידע אמיתי, לא על עותק בדמו." },
          { title: "אדם בלולאה וגדרות בטיחות", desc: "אישור אנושי על החלטות רגישות, אימות בעלות וגבולות ברורים — כדי שהסוכן יעשה רק את מה שמותר לו." },
          { title: "בנוי לפרודקשן ומנוטר", desc: "בדיקות, טיפול בשגיאות וניטור מלא — כדי שהסוכנים ימשיכו לעבוד אמין גם בעומס אמיתי." }
        ],
        stack: ["Claude", "Gemini", "OpenAI", "RAG + Vector DB", "LangGraph", "Function Calling", "Python", "Node.js", "REST APIs", "Webhooks", "Azure Cloud"],
        useCases: [
          { title: "שירות לקוחות אוטומטי", desc: "סוכן AI שמטפל ברוב הפניות לבד ומעביר לנציג רק את מה שבאמת דורש יד אנושית — זמן טיפול ממוצע שירד מ-12 דקות לפחות מ-90 שניות." },
          { title: "פלטפורמת PersonaAI", desc: "סוכן אג'נטי אחד שמנהל מכירות, שירות ותמיכה בשישה ערוצים (וואטסאפ, צ'אט באתר, אינסטגרם, מסנג'ר, טלגרם ואימייל), מחובר למערכות החיות של כל עסק." },
          { title: "החזרים בתעופה", desc: "מנוע AI שמחובר ל-Amadeus ומטפל בבקשות החזר מורכבות על פני יותר מ-20 תרחישים — שניות במקום דקות ארוכות, וכמעט אפס טעויות." },
          { title: "מנוע תמחור אוטומטי", desc: "עבור סיטונאי חלפים בנינו מנוע שמעבד אלפי מחירי ספקים, מיישם חוקי תמחור ומרווחים, מזהה חריגות ומעדכן את הקטלוג אוטומטית — תמחור שהיה ידני ומועד לטעויות, עכשיו רץ לבד." }
        ],
        benefits: [
          "AI שמחובר למערכות האמיתיות שלכם — לא עוד דמו",
          "פחות עבודה ידנית ופחות טעויות אנוש",
          "אדם בלולאה על ההחלטות שחשובות",
          "זמן טיפול שמתקצר דרמטית — דקות שהופכות לשניות",
          "בנוי על 20 שנות ניסיון באינטגרציה — אמין ויציב גם בעומס אמיתי"
        ],
        integrations: ["Amadeus", "Priority", "SAP", "Shopify", "WooCommerce", "AfterShip", "Twilio", "WhatsApp", "Google Calendar"],
        caseStudyHint: "ראו איך בנינו את פלטפורמת PersonaAI ואת מנוע ה-AI להחזרים בתעופה — בסיפורי ההצלחה והמשאבים שלנו."
      },
      integration: {
        title: "שירותי אינטגרציה בין מערכות",
        subtitle: "חיבור מערכות ERP למסחר, סנכרון נתונים בזמן אמת ואוטומציה של תהליכים — הבסיס הנקי והאמין שכל AI צריך כדי לעבוד.",
        paragraphs: [
          "כשהמערכות שלכם לא מדברות אחת עם השנייה, אתם מבזבזים זמן, כסף ועצבים. הצוות שלכם מקליד נתונים פעמיים, הלקוחות מקבלים מידע לא מעודכן, והמלאי לעולם לא מדויק. אנחנו בונים את החיבורים שגורמים לכל המערכות לעבוד יחד בצורה חלקה.",
          "הניסיון שלנו כולל אינטגרציות מורכבות בין מערכות ERP מובילות (Priority, SAP Business One) לפלטפורמות מסחר (Shopify Plus, WooCommerce), חיבור לספקי תשלום ושליחים, סנכרון נתונים ממספר ספקים למערכת אחת מאוחדת, ואינטגרציה עם מערכות שיווק ו-CRM.",
          "עבדנו עם מותגים מובילים כמו Skechers Israel, MegaSport ו-JD Sports — ובנינו עבורם אינטגרציות שחסכו אלפי שעות עבודה בשנה וחיסלו לחלוטין את הטעויות בהזמנות.",
          "התוצאה? תהליכים אוטומטיים מקצה לקצה, אפס עבודה ידנית כפולה, ונתונים נקיים ומסונכרנים בזמן אמת — בדיוק הבסיס שמאפשר גם ל-AI לעבוד על מידע אמין."
        ],
        features: [
          { title: "ERP לאיקומרס", desc: "חיבור Priority/SAP לחנויות Shopify עם סנכרון מלאי, הזמנות, לקוחות ומחירים בזמן אמת." },
          { title: "תשלומים ושליחים", desc: "אינטגרציה מלאה עם Tranzila, PayPal, PayMe ושירותי משלוחים כמו HFD, Baldar ו-Bpost." },
          { title: "שיווק ו-CRM", desc: "חיבור ל-Klaviyo, Smile.io, ActiveCampaign ומערכות נאמנות לקוחות." },
          { title: "סנכרון בזמן אמת", desc: "עדכונים אוטומטיים דו-כיווניים תוך שניות, עם מערכת retry חכמה וטיפול בשגיאות." },
          { title: "דוחות וניטור", desc: "דשבורדים לצפייה בסטטוס האינטגרציה, לוגים מפורטים והתראות על בעיות." },
          { title: "איחוד מקורות נתונים", desc: "משיכת נתונים ממספר ספקים ומערכות לתוך מערכת אחת מאוחדת ומנורמלת." }
        ],
        stack: ["Priority ERP", "SAP Business One", "Shopify Plus API", "WooCommerce", "REST APIs", "Webhooks", "Real-time Sync", "Azure Cloud"],
        useCases: [
          { title: "רשת קמעונאות ספורט", desc: "אינטגרציה מלאה בין Priority לשופיפיי עם סנכרון מלאי רב-מחסני, הזמנות אוטומטיות וניהול מחירים דינמי." },
          { title: "מותג אופנה בינלאומי", desc: "חיבור מערכת ERP גלובלית לחנות מקומית עם תמיכה במטבעות מרובים ותרגום אוטומטי." },
          { title: "יבואן מוצרים", desc: "איחוד קטלוגים ממספר ספקים בינלאומיים למערכת אחת עם עדכוני מחירים ומלאי אוטומטיים." }
        ],
        benefits: [
          "חיסכון של 20+ שעות עבודה ידנית בשבוע",
          "הפחתת טעויות בהזמנות ב-95%",
          "מלאי מדויק ב-100% בכל הערוצים",
          "זמן תגובה לשינויים — שניות במקום שעות",
          "שקט נפשי עם מערכת אמינה שעובדת 24/7"
        ],
        integrations: ["Priority", "SAP", "Shopify", "WooCommerce", "Tranzila", "PayPal", "Klaviyo", "HFD", "Baldar"],
        caseStudyHint: "ראו איך בנינו אינטגרציה מלאה עבור Skechers Israel ו-MegaSport בדפי סיפורי ההצלחה שלנו."
      },
      'custom-software': {
        title: "פיתוח תוכנה בהתאמה אישית",
        subtitle: "כשמוצרי מדף לא מספיקים, אנחנו בונים את הפלטפורמה, האפליקציה או המערכת שאתם באמת צריכים — עם AI מוטמע במקום שעוזר.",
        paragraphs: [
          "יש לכם צורך עסקי ייחודי שאף מוצר קיים לא פותר? זה בדיוק מה שאנחנו עושים. אנחנו בונים מערכות מורכבות מאפס — פלטפורמות B2B, מרקטפלייסים, אפליקציות מובייל ומערכות מידע ייחודיות.",
          "בנינו פלטפורמת B2B שמאחדת מוצרים מ-3 ספקי אופנה אירופיים שונים למערכת אחת עם ניהול הזמנות bulk ו-dropshipping. בנינו מרקטפלייס טבעוני מלא עם עשרות מוכרים, מערכת משלוחים בזמן אמת ותשלומי ספקים אוטומטיים.",
          "מה שמבדיל אותנו הוא ה-Framework הייחודי שלנו — אוסף של רכיבים, כלים ומודולים מוכחים שבנינו לאורך שנים של פרויקטים מורכבים. במקום להתחיל מאפס בכל פעם, אנחנו ממנפים את הבסיס הזה כדי להאיץ את הפיתוח משמעותית. המשמעות — אספקה מהירה יותר, עלויות נמוכות יותר ואמינות מוכחת מהיום הראשון.",
          "אנחנו גם משלבים AI בתהליך הפיתוח עצמו — ככלי שמאיץ את העבודה, וכיכולת שאנחנו מטמיעים בתוך המוצר כשהיא מוסיפה ערך אמיתי: חיפוש חכם, סיווג, חיזוי ועיבוד שפה.",
          "אנחנו לא מאמינים בפתרונות גנריים. כל פרויקט מתחיל באפיון מעמיק של הצרכים שלכם, כולל הבנה של התהליכים העסקיים, נקודות הכאב והיעדים. רק אחרי שאנחנו מבינים באמת מה אתם צריכים, אנחנו מתחילים לבנות.",
          "התוצאה? מערכת שעובדת בדיוק כמו שאתם צריכים, לא מערכת שאתם צריכים להתאים את עצמכם אליה."
        ],
        features: [
          { title: "פלטפורמות B2B", desc: "מערכות הפצה, פורטלים לסוכנים, ניהול קטלוגים מורכבים וממשקי לקוחות עסקיים." },
          { title: "מרקטפלייסים", desc: "שווקים מקוונים מלאים עם ניהול מוכרים, מוצרים, הזמנות ועמלות אוטומטיות." },
          { title: "פורטלי ספקים", desc: "ממשקי fulfillment לניהול הזמנות, אריזה ומשלוחים עם סריקת ברקודים." },
          { title: "דשבורדים בזמן אמת", desc: "מערכות ניטור ואנליטיקה עם WebSockets לעדכונים מיידיים." },
          { title: "אפליקציות מובייל", desc: "פיתוח React Native cross-platform לאפליקציות iOS ו-Android." },
          { title: "אינטגרציות מורכבות", desc: "חיבור לספקים חיצוניים — FTP, SOAP, REST, CSV וכל פורמט אחר." }
        ],
        stack: ["React", "React Native", "Angular", "Node.js", "ASP.NET Core", "PostgreSQL", "SQL Server", "Azure Cloud", "SignalR", "CS-Cart"],
        useCases: [
          { title: "פלטפורמת אופנה B2B", desc: "איחוד מוצרים מ-3 ספקים אירופיים, ניהול הזמנות bulk/dropship, פורטל ספקים ואפליקציית שופיפיי." },
          { title: "מרקטפלייס טבעוני", desc: "מרקטפלייס עם עשרות מוכרים, מערכת משלוחים בזמן אמת, חישוב תשלומי ספקים אוטומטי." },
          { title: "מערכת ניהול הפצה", desc: "פלטפורמה לניהול הזמנות מרובות מחסנים עם אופטימיזציית מסלולים." },
          { title: "מערכת תמחור לסיטונאי", desc: "מערכת תמחור מלאה לסיטונאי חלקי רובוטיקה שמעבדת אלפי מחירים." }
        ],
        benefits: [
          "זמן הגעה לשוק מהיר יותר עם ספריית הרכיבים המוכחת שלנו",
          "מערכת מותאמת ב-100% לתהליכים שלכם",
          "יתרון תחרותי עם טכנולוגיה ייחודית",
          "אפשרות לצמיחה ללא הגבלות של פלטפורמה",
          "בעלות מלאה על הקוד והמערכת",
          "תמיכה וליווי לטווח ארוך"
        ],
        caseStudyHint: "ראו את הפלטפורמות שבנינו עבור Luxhype Fashion ו-Vegan Place Club בדפי סיפורי ההצלחה."
      },
      common: {
        backToHome: "חזרה לדף הבית",
        professionalOverview: "סקירה מקצועית",
        techCapabilities: "טכנולוגיות ויכולות",
        readyToScale: "מוכנים לשדרג את העסק?",
        readyToScaleDesc: "בואו נדבר על הפרויקט שלכם ונבנה יחד את הפתרון המושלם.",
        bookConsultation: "תיאום שיחת ייעוץ",
        whyChooseUs: "מדוע לבחור בנו?",
        experienceTopBrands: "ניסיון עם המותגים הגדולים בישראל",
        longTermSupport: "תמיכה וליווי לטווח ארוך",
        viewCaseStudies: "לסיפורי ההצלחה",
        successStoriesTitle: "סיפורי הצלחה",
        successStoriesDesc: "ראו איך עזרנו לעסקים כמו שלכם"
      }
    },
    process: {
      title: "תהליך הפיתוח המוכח שלנו",
      subtitle: "מהקונספט ועד ההשקה, אנחנו מלווים אתכם בכל שלב בדרך",
      steps: [
        { 
          title: "פגישת היכרות", 
          desc: "אנחנו לומדים את העסק, המטרות והאתגרים שלכם",
          deliverables: ["הבנה מעמיקה של הצרכים", "זיהוי נקודות כאב", "הצעת כיוון ראשוני"],
          clientAction: "שיתוף החזון והאתגרים העסקיים"
        },
        { 
          title: "אפיון ותכנון", 
          desc: "צלילה עמוקה לדרישות ובניית מפרט טכני מפורט",
          deliverables: ["מסמך אפיון מלא", "ארכיטקטורת מערכת", "הצעת מחיר מפורטת"],
          clientAction: "אישור האפיון והיקף הפרויקט"
        },
        { 
          title: "עיצוב ופיתוח", 
          desc: "פיתוח אג'ילי עם עדכונים שוטפים ופידבק מתמיד",
          deliverables: ["גרסאות דמו להתרשמות", "סביבת בדיקות", "עדכוני התקדמות שבועיים"],
          clientAction: "מתן פידבק ואישור שלבים"
        },
        { 
          title: "בדיקות ואיכות", 
          desc: "בדיקות קפדניות להבטחת איכות, ביצועים ואבטחה",
          deliverables: ["דוחות בדיקות מלאים", "תיקון באגים", "אופטימיזציית ביצועים"],
          clientAction: "בדיקות קבלה ואישור סופי"
        },
        { 
          title: "השקה ותמיכה", 
          desc: "פריסה חלקה לייצור עם ליווי מתמשך ותמיכה טכנית",
          deliverables: ["העלאה לייצור", "הדרכה לצוות", "תמיכה שוטפת"],
          clientAction: "אימות תקינות בסביבת הייצור"
        }
      ],
      cta: "בואו נתחיל יחד",
      ctaSubtext: "בואו נדבר ונבנה יחד את הפתרון המושלם",
      badge: "איך אנחנו עובדים",
      details: "לפרטים",
      whatYouGet: "מה תקבלו",
      yourPart: "מה נדרש מכם",
      ctaButton: "בואו נדבר",
      stats: {
        projects: "פרויקטים",
        satisfaction: "שביעות רצון",
        years: "שנות ניסיון",
        response: "זמן תגובה"
      }
    },
    whyUs: {
      badge: "למה אנחנו?",
      title: "כשפתרונות מוכנים לא מספיקים",
      desc: "אנחנו בונים את מה שאחרים לא יכולים — מאינטגרציות מורכבות שפלאגינים לא פותרים, דרך אוטומציה חכמה שחוסכת שעות עבודה, ועד פלטפורמות מותאמות בדיוק לעסק שלכם.",
      differentiators: [
        {
          title: "פותרים בעיות קשות",
          desc: "כשפתרונות מדף נכשלים ופלאגינים נשברים, אנחנו בונים מה שבאמת עובד"
        },
        {
          title: "גישה ישירה למפתחים",
          desc: "אתם מדברים ישירות עם צוות הפיתוח, לא עם אנשי מכירות או מתווכים"
        },
        {
          title: "ניסיון מגוון בפרויקטים",
          desc: "מאינטגרציות ERP, דרך אוטומציה עם AI ועד בניית פלטפורמות מלאות"
        },
        {
          title: "פתרונות מקצה לקצה",
          desc: "מאפיון ראשוני, דרך פיתוח ופריסה ועד תמיכה שוטפת — אנחנו מטפלים בהכל"
        }
      ],
      founderName: "מיכאל אופנגנדן",
      founderTitle: "מייסד ומנכ\"ל Ronnie Projects",
      founderQuote: "אני אוהב לבנות דברים שעובדים. אני אוהב לעזור לעסקים לצמוח. ובכנות? אני אוהב כשלקוחות מרוצים. זה לא שיווק — ככה אנחנו עובדים.",
      cta: "התחילו את הפרויקט",
      commitmentTitle: "המחויבות שלנו",
      commitmentDesc: "מצוינות הנדסית",
      uptime: "זמינות מערכות"
    },
    contact: {
      title: "מוכנים לשדרג את העסק?",
      subtitle: "ספרו לנו על הפרויקט שלכם. ללא התחייבות, רק ייעוץ מקצועי.",
      callUs: "התקשרו אלינו",
      emailUs: "שלחו לנו מייל",
      form: {
        name: "שם מלא",
        email: "כתובת אימייל",
        phone: "טלפון",
        company: "חברה",
        interest: "במה נוכל לעזור?",
        message: "ספרו לנו קצת על הפרויקט",
        submit: "שליחת הבקשה",
        options: ["סוכני AI", "פיתוח תוכנה בהתאמה אישית", "אינטגרציה בין מערכות", "אחר"],
        successTitle: "הבקשה התקבלה!",
        successDesc: "תודה על פנייתך. אחד המומחים שלנו יחזור אליך תוך יום עסקים אחד.",
        validation: {
          nameRequired: "נא להזין שם",
          nameMinLength: "השם חייב להכיל לפחות 2 תווים",
          nameMaxLength: "השם לא יכול להכיל יותר מ-100 תווים",
          emailRequired: "נא להזין כתובת אימייל",
          emailInvalid: "כתובת האימייל אינה תקינה",
          phoneRequired: "נא להזין מספר טלפון",
          phoneInvalid: "מספר הטלפון אינו תקין",
          companyMaxLength: "שם החברה לא יכול להכיל יותר מ-100 תווים",
          interestRequired: "נא לבחור תחום עניין",
          messageMaxLength: "ההודעה לא יכולה להכיל יותר מ-2000 תווים"
        }
      },
      recommendation: {
        quote: "אנחנו אוהבים את מה שאנחנו עושים — לעזור לעסקים להצליח ולפתור את האתגרים שאחרים אומרים שהם מסובכים מדי. בואו נדבר.",
        author: "מיכאל אופנגנדן",
        position: "מנכ\"ל ומייסד Ronnie Projects"
      }
    },
    footer: {
      desc: "פתרונות תוכנה ואינטגרציה למותגי פרימיום. אנחנו מחברים בין טכנולוגיה לעסקים.",
      copyright: "כל הזכויות שמורות ל-Ronnie Projects.",
      servicesTitle: "שירותים",
      companyTitle: "חברה",
      contactTitle: "צור קשר",
      aiAgents: "סוכני AI",
      customSoftware: "פיתוח תוכנה בהתאמה אישית",
      systemIntegration: "אינטגרציה בין מערכות",
      about: "אודות",
      articles: "מאמרים",
      caseStudies: "סיפורי הצלחה",
      resources: "משאבים",
      contact: "צור קשר",
      privacy: "מדיניות פרטיות",
      terms: "תנאי שימוש",
      accessibility: "הצהרת נגישות",
      location: "מודיעין, ישראל",
      whatsappChat: "צ׳אט בוואטסאפ",
      ctaTitle: "מוכנים להתחיל?",
      ctaButton: "התחילו את הפרויקט",
      followLinkedin: "עקבו אחרינו בלינקדאין (נפתח בחלון חדש)",
      followFacebook: "עקבו אחרינו בפייסבוק (נפתח בחלון חדש)",
      followInstagram: "עקבו אחרינו באינסטגרם (נפתח בחלון חדש)",
      comingSoon: "בקרוב",
      socialLinkComingSoon: "הקישור יתווסף בקרוב"
    },
    ai: {
      welcome: "שלום! אני היועץ החכם של Ronnie Projects. אפשר לשאול אותי על סוכני AI, אינטגרציה בין מערכות או פיתוח תוכנה.",
      placeholder: "שאלו אותי על הפרויקט שלכם...",
      online: "מחובר - ייעוץ טכני מיידי"
    },
    logoMarquee: {
      badge: "שותפים להצלחה",
      titlePart1: "מותגים שבחרו",
      titleHighlight: "לעבוד איתנו",
      subtitle: "גאים לעבוד עם מותגים מובילים בישראל ובעולם",
      ctaQuestion: "רוצים להצטרף לרשימת הלקוחות המרוצים?",
      ctaLink: "בואו נדבר"
    },
    notFound: {
      errorCode: "404",
      title: "אופס! הדף לא נמצא",
      subtitle: "נראה שהלכתם לאיבוד בחלל הדיגיטלי",
      description: "הדף שחיפשתם לא קיים או שהועבר למיקום אחר. אל דאגה — אנחנו כאן לעזור לכם למצוא את מה שאתם מחפשים.",
      backHome: "חזרה לדף הבית",
      contact: "צור קשר",
      popularPages: "דפים פופולריים",
      services: "השירותים שלנו",
      about: "אודותינו",
      aiAgents: "סוכני AI",
      integration: "אינטגרציה בין מערכות",
      customDev: "פיתוח תוכנה בהתאמה אישית",
      searchHint: "בינתיים, הנה כמה קישורים שיכולים לעזור:"
    },
    testimonials: {
      title: "מה הלקוחות שלנו אומרים",
      subtitle: "שותפים למותגים המובילים בישראל בדרך לשינוי דיגיטלי.",
      items: [
        {
          quote: "מיכאל יישם בהצלחה אינטגרציה קריטית בין מערכת ה-ERP Priority לחנות השופיפיי שלנו, תוך הפגנת מומחיות טכנית וכישורי הבנה עסקית יוצאי דופן. האינטגרציה ייעלה משמעותית את התפעול שלנו והביאה לחיסכון בזמן ולהפחתת טעויות.",
          name: "רובי בוגי'צי",
          title: "מנהל מסחר אלקטרוני",
          company: "Skechers Israel",
          image: "https://media.licdn.com/dms/image/v2/D4D03AQG-GVK4N3Bwrg/profile-displayphoto-shrink_100_100/profile-displayphoto-shrink_100_100/0/1672220283922?e=1743638400&v=beta&t=7dhqZ7ypqV6YHAhXfLyN4vt8hwYg-AzUkU9Fo_XAo8M"
        },
        {
          quote: "לי ולצוות שלי היה העונג לעבוד עם מיכאל על פרויקט אינטגרציה מורכב עבור פלטפורמת המסחר האלקטרוני שלנו. מיכאל הפגין מומחיות יוצאת דופן בפיתוח Backend ו-Frontend, וחיבר בצורה חלקה את חנות השופיפיי שלנו למערכת ה-Priority.",
          name: "חי דיק",
          title: "מנהל דיגיטל",
          company: "JD Sports Israel",
          image: "https://media.licdn.com/dms/image/v2/D4D03AQEXf6loAhVFyg/profile-displayphoto-shrink_100_100/profile-displayphoto-shrink_100_100/0/1674560564818?e=1743638400&v=beta&t=5XpRrQiH9hyhPh2MdmT3Iy-1D20p0QVF0d4mA_wB_Mo"
        },
        {
          quote: "אני ממליץ בחום על Ronnie Projects לכל עסק שזקוק לשירותי פיתוח תוכנה אמינים, מיומנים וממוקדי לקוח. המקצועיות שלהם, הכישרון הטכני והמחויבות למצוינות הם באמת יוצאי דופן.",
          name: "שמוליק קונפורטי",
          title: "CIO | CTO | מומחה Priority ERP",
          company: "One Project",
          image: "https://media.licdn.com/dms/image/v2/C4D03AQGaVNM2SFP9Eg/profile-displayphoto-shrink_100_100/profile-displayphoto-shrink_100_100/0/1652171471036?e=1743638400&v=beta&t=3_E9SZ5TKpSMAvGP6kAP3GWCuH57xqJVxVB9yFLnVFA"
        }
      ]
    },
    logoBar: {
      title: "הלקוחות שלנו"
    },
    accessibility: {
      title: "תפריט נגישות",
      close: "סגור",
      reset: "איפוס",
      fontSize: "גודל טקסט",
      increase: "הגדלה",
      decrease: "הקטנה",
      highContrast: "ניגודיות גבוהה",
      highlightLinks: "הדגשת קישורים",
      readableFont: "גופן קריא",
      pauseAnimations: "השהיית אנימציות",
      statement: "הצהרת נגישות",
      buttonLabel: "פתח תפריט נגישות"
    },
    articlesPage: {
      badge: "תובנות",
      title: "מאמרים",
      subtitle: "פרספקטיבות מקצועיות על אינטגרציה, אוטומציה ופיתוח מותאם לעסקים בצמיחה.",
      readMore: "קרא עוד",
      noArticles: "אין עדיין מאמרים זמינים. בדקו שוב בקרוב!",
      ctaText: "יש לכם שאלה? בואו נדבר.",
      ctaButton: "צרו קשר"
    },
    seo: {
      siteName: "Ronnie Projects",
      home: {
        title: "Ronnie Projects – סוכני AI, אינטגרציה ופיתוח תוכנה",
        description: "אנחנו בונים סוכני AI, מחברים אותם למערכות האמיתיות שלכם, ומפתחים תוכנה שמוצרי מדף לא יכולים לספק. נבנה כדי להחזיק מעמד."
      },
      about: {
        title: "אודותינו | Ronnie Projects",
        description: "למדו על Ronnie Projects - חברת פיתוח תוכנה עם 20+ שנות ניסיון באינטגרציות מסחר אלקטרוני ופיתוח מותאם."
      },
      aiAgents: {
        title: "סוכני AI | Ronnie Projects",
        description: "אנחנו בונים סוכני AI ועוזרים חכמים ומחברים אותם למערכות האמיתיות שלכם — ERP, מסחר ונתונים — כך שהם עושים עבודה אמיתית ועובדים באמת."
      },
      integrationServices: {
        title: "אינטגרציה בין מערכות | Ronnie Projects",
        description: "שירותי אינטגרציה מקצועיים - חיבור מערכות ERP, CRM, מסחר אלקטרוני ועוד. Priority, SAP, Shopify ונתונים נקיים שה-AI שלכם יכול לעבוד איתם."
      },
      customDevelopment: {
        title: "פיתוח תוכנה בהתאמה אישית | Ronnie Projects",
        description: "פיתוח תוכנה בהתאמה אישית - פלטפורמות B2B, מרקטפלייסים, אפליקציות ומערכות מורכבות, עם AI מוטמע במקום שעוזר."
      },
      contact: {
        title: "צור קשר | Ronnie Projects",
        description: "צרו קשר עם Ronnie Projects - ייעוץ חינם, הצעת מחיר ותחילת פרויקט. טלפון: 074-760-2660."
      },
      caseStudies: {
        title: "סיפורי הצלחה | Ronnie Projects",
        description: "סיפורי הצלחה של לקוחות Ronnie Projects - JD Sports, Skechers, MegaSport ועוד."
      },
      articles: {
        title: "מאמרים | Ronnie Projects",
        description: "מאמרים מקצועיים על אינטגרציה, אוטומציה ופיתוח מותאם לעסקים בצמיחה."
      },
      resources: {
        title: "משאבים | Ronnie Projects",
        description: "תובנות, מדריכים ומאמרים שיעזרו לכם לקבל החלטות טכנולוגיות טובות יותר לעסק."
      },
      privacy: {
        title: "מדיניות פרטיות | Ronnie Projects",
        description: "מדיניות הפרטיות של Ronnie Projects."
      },
      terms: {
        title: "תנאי שימוש | Ronnie Projects",
        description: "תנאי השימוש של Ronnie Projects."
      },
      accessibility: {
        title: "הצהרת נגישות | Ronnie Projects",
        description: "הצהרת הנגישות של Ronnie Projects."
      },
      caseStudyDetail: {
        title: "סיפור הצלחה: {slug} | Ronnie Projects",
        description: "קראו את סיפור ההצלחה של {slug} עם Ronnie Projects."
      },
      articleDetail: {
        title: "{slug} | Ronnie Projects",
        description: "קראו על {slug} ב-Ronnie Projects."
      },
      resourceDetail: {
        title: "{slug} | Ronnie Projects",
        description: "קראו על {slug} ב-Ronnie Projects."
      },
      notFoundPage: {
        title: "דף לא נמצא | Ronnie Projects",
        description: "הדף המבוקש לא נמצא."
      }
    },
    adminContent: {
      nav: {
        drafts: "Content Drafts",
        topics: "Topics",
        pipelineRunning: "Running pipeline…",
        pipelineSuccess: "Pipeline started — new draft will appear shortly",
        pipelineError: "Pipeline failed",
        dismiss: "Dismiss"
      },
      contentDrafts: {
        title: "Content Drafts",
        runPipeline: "Run Pipeline",
        runPipelineDryRun: "Dry Run",
        emptyState: "No drafts yet. Run the pipeline to generate the first one.",
        tabs: { all: "All", pending: "Pending", approved: "Approved", published: "Published", rejected: "Rejected", failed: "Failed" },
        columns: { title: "Title", topic: "Topic", generated: "Generated", status: "Status", actions: "Actions" },
        status: { pending: "Pending", approved: "Approved", published: "Published", rejected: "Rejected", failed: "Failed" },
        actions: { review: "Review", reject: "Reject" }
      },
      draftReview: {
        backToList: "← Drafts",
        tabs: { enArticle: "EN Article", heArticle: "HE Article", linkedinPost: "LinkedIn Post", seoMeta: "SEO Meta", cardPreview: "Card Preview" },
        actions: { saveEdits: "Save Edits", regenerate: "Re-generate", reject: "Reject", approveWebsiteOnly: "Approve — Website Only", approveAndPublish: "Approve & Publish" },
        publishTarget: { label: "Publish to", articles: "Articles", resources: "Resources" },
        rejectModal: { title: "Reject Draft", placeholder: "Reason for rejection…", confirm: "Reject", cancel: "Cancel" },
        seoScore: { title: "SEO Score", wordCount: "Word count 900–1200", keywordInH1: "Keyword in H1", keywordInFirst100: "Keyword in first 100 words", metaLength: "Meta description length", internalLinks: "Internal links present" },
        metadata: { topic: "Topic", keywords: "Keywords", sources: "Sources", estimatedCost: "Est. cost", generatedAt: "Generated" },
        autoSaving: "Saving…",
        saved: "Saved",
        charCount: "chars",
        linkedinPanel: {
          title: "LinkedIn",
          notScheduled: "Not scheduled",
          scheduleBtn: "Schedule for LinkedIn",
          postNowBtn: "Post Now",
          changeBtn: "Change",
          cancelBtn: "Cancel Post",
          scheduledFor: "Scheduled for",
          publishedAt: "Published on",
          failedWith: "Failed:",
          confirmTitle: "Pick date & time",
          confirmSave: "Save",
          confirmCancel: "Cancel",
        },
      },
      contentTopics: {
        title: "Content Topics",
        createNew: "New Topic",
        emptyState: "No topics yet. Create one to start the pipeline.",
        columns: { title: "Title (EN)", angle: "Angle", priority: "Priority", status: "Status", actions: "Actions" },
        status: { active: "Active", inProgress: "In Progress", done: "Done", paused: "Paused" },
        angles: { educational: "Educational", caseStudy: "Case Study", thoughtLeadership: "Thought Leadership", howTo: "How-To", newsReaction: "News Reaction" },
        languageNames: { he: "Hebrew", fr: "French", ar: "Arabic", de: "German", es: "Spanish", it: "Italian", pt: "Portuguese", zh: "Chinese", ja: "Japanese", ru: "Russian", tr: "Turkish" },
        form: { titleEn: "Title (EN)", keywordsEn: "Keywords EN (comma-separated)", translations: "Translations", translationsAdd: "Add Language", translationsLang: "Language", translationsTitle: "Title (optional)", translationsKeywords: "Keywords (comma-separated, optional)", translationsRemove: "Remove", angle: "Content Angle", audience: "Target Audience", priority: "Priority (1=high, 5=low)", notes: "Notes", isRecurring: "Recurring topic", status: "Status", save: "Save", cancel: "Cancel" },
        actions: { edit: "Edit", delete: "Delete" },
        deleteConfirm: "Delete this topic?"
      },
      helpTips: {
        topics: {
          page: "Topics are the content ideas that feed the AI pipeline. The pipeline picks the highest-priority Active topic and generates a full bilingual article + LinkedIn post.",
          angle: "The editorial lens for the article.\n• Educational — explain a concept\n• Case Study — tell a client story\n• Thought Leadership — share an opinion\n• How-To — step-by-step guide\n• News Reaction — respond to an industry event",
          priority: "1 = pick first, 5 = pick last. The pipeline always selects the lowest-numbered Active topic that hasn't been used recently.",
          status: "Only Active topics are eligible to be picked up by the pipeline. Set to Paused to skip a topic temporarily, or Done once it's been published and retired.",
          isRecurring: "When checked, the topic returns to Active after its draft is published, so it can generate fresh content on the same theme again in the future.",
          keywordsEn: "Comma-separated English keywords. The AI uses these to optimise titles, headings, and meta tags for search engines.",
          translations: "Add translations for each language you want to publish in. Each entry is optional — leave title or keywords blank to let the AI generate them from the English version.",
          audience: "Describe who this article is for — e.g. 'CTOs at mid-size retail brands'. The AI tailors its tone, examples, and level of technical detail accordingly.",
          notes: "Internal notes for your team only. These are not sent to the AI and do not appear in the generated article.",
        },
        drafts: {
          page: "Drafts are AI-generated articles waiting for your review. The pipeline runs Gemini (research + outline) then Claude (write + polish) to produce a bilingual article, LinkedIn post, and SEO metadata.",
          runPipeline: "Fetches trending industry sources, picks the top Active topic, and generates a full bilingual article using Gemini + Claude. A new draft will appear in the Pending tab within a minute.",
          dryRun: "Simulates the full pipeline without saving anything to the database. Use this to verify that sources are reachable and settings are correct before a real run.",
        },
        draftReview: {
          regenerate: "Re-runs Claude Phases 2 and 3 using the stored Gemini outline. Useful if you want a fresh take on the writing without re-researching sources. The previous text will be overwritten.",
          reject: "Rejects this draft and automatically returns the topic to the Active queue so the pipeline can pick it up again.",
          tabEn: "The full English article in Markdown format. Use # for the H1 title and ## for section headings. Changes are saved automatically.",
          tabHe: "The full Hebrew article in Markdown format, written right-to-left. Changes are saved automatically.",
          tabLinkedin: "A ready-to-post LinkedIn update. Aim for under 1,300 characters for best reach — the character counter below helps.",
          tabSeoMeta: "Meta title (keep under 70 chars) and meta description (150–160 chars). These appear in Google search results and social sharing previews.",
          tabCardPreview: "The case-study card shown on listing pages — brand, industry, headline, summary, tags, and color. AI fills these on generation; edit per language and click 'Regenerate Card' for a fresh AI pass.",
          seoScore: "A quick checklist of the five most important on-page SEO signals. Aim for 4/5 or higher before approving.",
        },
      },
      settings: {
        title: "הגדרות",
        languages: {
          title: "שפות האתר",
          description: "ניהול שפות האתר. השפה הברירת מחדל היא אנגלית ולא ניתן לשנות זאת.",
          code: "קוד",
          nameEn: "שם באנגלית",
          nameNative: "שם מקומי",
          isRtl: "ימין לשמאל",
          isActive: "פעיל",
          defaultBadge: "ברירת מחדל",
          addTitle: "הוסף שפה",
          codePlaceholder: "לדוגמה: fr",
          nameEnPlaceholder: "לדוגמה: French",
          nameNativePlaceholder: "לדוגמה: Français",
          save: "שמור",
          cancel: "ביטול",
          deleteConfirm: "למחוק שפה זו?",
          cannotDeleteDefault: "לא ניתן למחוק את שפת ברירת המחדל",
        },
      },
      linkedinSchedule: {
        title: "LinkedIn Schedule",
        connection: { connected: "Connected", partial: "Partial — missing Author URN", notConnected: "Not Connected", connectBtn: "Connect LinkedIn" },
        scheduleNewBtn: "Schedule New Post",
        monthly: "Monthly",
        weekly: "Weekly",
        status: { not_scheduled: "Not scheduled", scheduled: "Scheduled", published: "Published", failed: "Failed", pending: "Not scheduled" },
        modal: { title: "Schedule LinkedIn Post", selectDraft: "Select draft", dateTime: "Date & time", save: "Schedule", cancel: "Cancel", noDrafts: "No approved drafts available to schedule." },
        editModal: { title: "Edit Schedule", newTime: "New date & time", save: "Save", cancelPost: "Cancel Post", close: "Close" },
        postNow: "Post Now",
        scheduledFor: "Scheduled for",
        publishedAt: "Published",
        noPostsInPeriod: "No posts scheduled for this period.",
        daysShort: ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"],
        months: ["January", "February", "March", "April", "May", "June", "July", "August", "September", "October", "November", "December"],
      },
      seoIntelligence: {
        title: "SEO Intelligence",
        refresh: "Refresh",
        generating: "Generating…",
        lastRefresh: "Last refresh:",
        never: "Never",
        days7: "7 days",
        days28: "28 days",
        days90: "90 days",
        keywords: {
          title: "Keyword Performance",
          query: "Query",
          page: "Page",
          clicks: "Clicks",
          impressions: "Impr.",
          ctr: "CTR",
          position: "Pos.",
          createArticle: "Create article",
        },
        pages: {
          title: "Top Pages",
          page: "Page",
          sessions: "Sessions",
          bounceRate: "Bounce %",
          avgDuration: "Avg Duration",
          declining: "Declining",
        },
        gaps: {
          title: "Content Gaps",
          description: "Keywords with >50 impressions and position >10 that have no matching published page.",
          empty: "No content gaps detected.",
        },
        recommendations: {
          title: "AI Recommendations",
          generate: "Generate Now",
          generatedAt: "Generated:",
          addToTopics: "Add to Topics",
          empty: "No recommendations yet. Click \"Generate Now\" to run the analysis.",
          priority: { high: "High", medium: "Medium", low: "Low" },
        },
        errors: {
          keywordsFailed: "Could not load keyword data. Check GOOGLE_SEARCH_CONSOLE_SITE_URL and service account permissions.",
          pagesFailed: "Could not load page data. Check GOOGLE_ANALYTICS_PROPERTY_ID and service account permissions.",
          notConfigured: "Google credentials not configured.",
        },
      },
      prompts: {
        title: "פרומפטים לפי שפה",
        intro: {
          title: "פרומפטים מותאמים אישית לכל שפה",
          description: "טקסט שתשמרו כאן ישמש כהנחיית סגנון בשלב הליטוש והתרגום, בכל פעם שמופק מאמר בשפה הזו. כשאין פרומפט מותאם, נעשה שימוש בברירת המחדל המובנית.",
        },
        empty: "אין שפות פעילות. הוסיפו שפות מתוך ההגדרות.",
        notSet: "טרם הוגדר פרומפט מותאם.",
        lastUpdated: "עודכן לאחרונה: {date}",
        chars: "תווים",
        placeholder: "כתבו כאן את מדריך הסגנון לשפה זו…",
        confirmDelete: "למחוק את הפרומפט המותאם עבור {lang}? המערכת תחזור להשתמש בברירת המחדל המובנית.",
        direction: { label: "כיוון:", ltr: "שמאל לימין", rtl: "ימין לשמאל" },
        badges: { custom: "מותאם אישית", default: "ברירת מחדל" },
        actions: { save: "שמור", reset: "שחזר", delete: "מחק" },
        toasts: { saved: "הפרומפט עבור {lang} נשמר", deleted: "הפרומפט עבור {lang} נמחק" },
        errors: {
          loadFailed: "טעינת הפרומפטים נכשלה.",
          saveFailed: "שמירת הפרומפט נכשלה.",
          deleteFailed: "מחיקת הפרומפט נכשלה.",
          emptyPrompt: "לא ניתן לשמור פרומפט ריק.",
        },
      },
    }
  },
  en: {
    nav: {
      services: "Services",
      articles: "Articles",
      caseStudies: "Case Studies",
      resources: "Resources",
      process: "Process",
      about: "About",
      contact: "Let's Talk",
      langToggle: "עברית"
    },
    hero: {
      badge: "Real AI. Real Systems.",
      headline: "We Build AI Into Your Business",
      subheadline: "We've spent 20 years solving the hardest part of AI — connecting it to your real systems. We build the agents on top — reliable, integrated, and built to last.",
      ctaPrimary: "Let's Talk",
      ctaSecondary: "Our Services",
      stats: {
        projects: "Projects Shipped",
        experience: "Years of Software Development",
        success: "Success Rate"
      },
      scrollDown: "Scroll Down",
      scrollDownAria: "Scroll down",
      systems: [
        {
          caption: "Luxhype Fashion · AI size mapping, human-approved",
          alt: "AI mapping suggestions comparing two model proposals with confidence scores, awaiting human approval"
        },
        {
          caption: "PersonaAI · Live customer conversations",
          alt: "AI agent handling a live customer conversation in a shared inbox"
        },
        {
          caption: "PriorityHub · Autonomous address correction",
          alt: "Change log of AI-corrected delivery addresses with confidence scores"
        }
      ],
      systemsAria: "Select a system to view"
    },
    problems: {
      badge: "Why Clients Come to Us",
      title: "Sound Familiar?",
      subtitle: "If this feels like you — you're not alone. These are exactly why clients reach out to us.",
      problems: [
        {
          title: "We've tried everything. Nothing works.",
          desc: "You've bought plugins, hired freelancers, tried connecting systems yourself. It's still broken, and you've wasted months."
        },
        {
          title: "We're drowning in manual work",
          desc: "Your team spends hours every day on copy-paste tasks, fixing order errors, and syncing data between systems."
        },
        {
          title: "No ready-made solution exists for what we need",
          desc: "You've searched. You've demoed. Nothing fits. You need something built specifically for your business."
        },
        {
          title: "We've already spent a fortune — and got nothing",
          desc: "Previous developers took forever, charged too much, and left you with half-working code. You need someone who delivers."
        },
        {
          title: "We don't even know where to start",
          desc: "The technology is confusing. The options are overwhelming. You need a guide, not just a developer."
        },
        {
          title: "Everyone else is slow and expensive",
          desc: "Other companies quoted months of work and huge budgets. You need a partner who moves fast without cutting corners."
        }
      ],
      cta: "Get Expert Help"
    },
    services: {
      title: "Our Services",
      subtitle: "We build what off-the-shelf can't — AI agents, custom software, and the integrations that connect them to your real systems.",
      crossCutting: "AI runs through all three: we build with it, embed it where it helps, and build it as a product when that's what you need.",
      aiAgents: {
        title: "AI Agents",
        desc: "We build AI agents and assistants that do real work — handling customer conversations, processing documents, and acting inside your live systems.",
        cta: "Learn More",
        benefits: [
          "Custom AI agents & assistants",
          "Document & data processing",
          "AI inside your live systems",
          "Built to stay reliable"
        ]
      },
      custom: {
        title: "Custom Software Development",
        desc: "When no product fits, we build it — platforms, marketplaces, mobile apps, and complex systems, with AI built in where it makes a difference.",
        cta: "Learn More",
        benefits: [
          "B2B platforms & marketplaces",
          "Mobile apps (iOS & Android)",
          "Complex custom systems",
          "AI built in where it helps"
        ]
      },
      integration: {
        title: "System Integration",
        desc: "We connect your systems — ERP (Priority, SAP), commerce, and more — with real-time sync and automated workflows that give your AI clean, reliable data to work with.",
        cta: "Learn More",
        benefits: [
          "ERP ↔ commerce connections",
          "Real-time data sync",
          "Automated workflows",
          "Clean data your AI can use"
        ]
      },
      benefitsTitle: "What We Offer:",
      badge: "Our Services",
      learnMore: "Learn More",
      notSure: "Not sure what's right for you?",
      talkToUs: "Talk to Us"
    },
    about: {
      title: "The Story of Ronnie Projects",
      subtitle: "We build what off-the-shelf solutions can't",
      storyTitle: "Our Story",
      story: [
        "Ronnie Projects specializes in solving complex technology problems — the kind that off-the-shelf products don't solve. We build system integrations, custom B2B platforms, smart automation solutions, and unique information systems.",
        "The founder, Michael Offengenden, has over two decades of software development experience. Throughout his career, he has served in senior management positions in leading companies, managed advanced development teams in startups and technology companies, and accumulated deep expertise in architecting and developing complex systems."
      ],
      teamTitle: "Our Team",
      team: "The Ronnie Projects team consists of top experts, including project managers, system analysts, Backend and Frontend developers, application developers, and QA professionals. When needed, we integrate additional consultants and developers to provide the best solutions for each project.",
      serviceTitle: "What We Do",
      service: "We specialize in three areas: AI Agents (custom agents and assistants connected to your live systems), Custom Software Development (B2B platforms, marketplaces, mobile apps, and complex systems), and System Integration (ERP to commerce, automated workflows, and clean data your AI can use). Every project is built with full customization to your unique needs.",
      valuesTitle: "Our Values",
      valuesDesc: "We believe every technology problem can be solved. Our approach starts with deep understanding of the business need, continues with precise specification, and ends with a solution that works exactly as needed.",
      values: [
        { title: "Solving Hard Problems", desc: "When off-the-shelf fails and plugins break — we build what actually works." },
        { title: "Full Customization", desc: "We don't believe in generic solutions. Every project is built exactly to your requirements." },
        { title: "Long-term Partnership", desc: "We are not just a software vendor; we are a strategic partner accompanying you at every step." }
      ],
      cta: "Get Free Quote",
      badge: "About Us",
      whoWeAre: "Who We Are",
      teamImageAlt: "Our Team at Work",
      yearsExperience: "Years Experience",
      techSolutionsAlt: "Technological Solutions",
      whatWeDo: "What We Do",
      ourPrinciples: "Our Principles",
      bookConsultation: "Let's Talk",
      ctaTitle: "Let's Supercharge Your Online Growth",
      ctaDesc: "Ready to get started? Contact us today and let's discuss how we can help you",
      stats: {
        yearsExp: "Years Experience",
        projects: "Projects Delivered",
        clients: "Happy Clients",
        success: "Success Rate"
      }
    },
    serviceDetail: {
      'ai-agents': {
        title: "AI Agents & Assistants",
        subtitle: "Custom AI agents that do real work inside your live systems — not another demo, but an agent that actually acts.",
        paragraphs: [
          "Everyone is talking about AI, but most solutions stay stuck in the demo — they can't touch the real systems that run your business, so they never really help. We build AI agents that connect to your systems and do real work, day after day: handling customer conversations, supporting your internal team, and turning raw data into automated decisions inside your systems.",
          "And the results speak for themselves. For an airline we built an AI engine wired directly into Amadeus that handles refund requests automatically — a process that took 15 to 30 minutes per request now takes seconds, with near-zero errors. Another customer-service agent we built cut average handling time from 12 minutes to under 90 seconds. And PersonaAI — an AI agent platform that runs sales, service, and support across six channels — we built from the ground up.",
          "The difference between a fragile automation script and a real AI agent is that the agent reasons, adapts, and decides: when conditions change it adjusts, and when data is missing it fills the gaps. But the hard part isn't the AI itself — it's connecting it safely and reliably to your ERP, your commerce platforms, and your live data. That's exactly what we've specialized in for 20 years.",
          "We build for production from day one: a mechanism that stops the agent from \"making up\" answers, a human in the loop on sensitive decisions, guardrails and ownership verification before any meaningful action, and full monitoring of everything the agent does. And we don't lock you into a single vendor — we pick the right AI model for each task."
        ],
        features: [
          { title: "Customer-Facing Agents", desc: "AI agents that hold conversations, answer questions, and handle requests — escalating to a human exactly when needed." },
          { title: "Internal Copilots", desc: "AI copilots that pull answers from your internal knowledge and systems, draft replies, and complete operational tasks — so your team stops digging and starts deciding." },
          { title: "From Data to Decisions", desc: "Agents that don't just read your data — they reason over it: applying your business rules, flagging anomalies, and pushing the right decision back into your systems." },
          { title: "Connected to Your Live Systems", desc: "Wire agents into your ERP, commerce, and data so they act on real information — not a demo copy." },
          { title: "Human-in-the-Loop & Guardrails", desc: "Human approval on sensitive decisions, ownership verification, and clear limits — so the agent only does what it's allowed to." },
          { title: "Built for Production, Monitored", desc: "Testing, error handling, and full monitoring so agents keep working reliably under real load." }
        ],
        stack: ["Claude", "Gemini", "OpenAI", "RAG + Vector DB", "LangGraph", "Function Calling", "Python", "Node.js", "REST APIs", "Webhooks", "Azure Cloud"],
        useCases: [
          { title: "Automated Customer Service", desc: "An AI agent that handles most inquiries on its own and escalates only what truly needs a human — average handling time cut from 12 minutes to under 90 seconds." },
          { title: "The PersonaAI Platform", desc: "A single agentic agent running sales, service, and support across six channels (WhatsApp, web chat, Instagram, Messenger, Telegram, and email), wired into each business's live systems." },
          { title: "Airline Refunds", desc: "An AI engine wired into Amadeus that handles complex refund requests across 20+ scenarios — seconds instead of long minutes, with near-zero errors." },
          { title: "Automated Pricing Engine", desc: "For a parts wholesaler we built an engine that ingests thousands of supplier prices, applies pricing rules and margins, flags anomalies, and updates the catalog automatically — pricing that used to be manual and error-prone now runs on its own." }
        ],
        benefits: [
          "AI connected to your real systems — not another demo",
          "Less manual work and fewer human errors",
          "A human in the loop on the decisions that matter",
          "Handling time that drops dramatically — minutes become seconds",
          "Built on 20 years of integration experience — reliable under real load"
        ],
        integrations: ["Amadeus", "Priority", "SAP", "Shopify", "WooCommerce", "AfterShip", "Twilio", "WhatsApp", "Google Calendar"],
        caseStudyHint: "See how we built the PersonaAI platform and the airline refund AI engine in our case studies and resources."
      },
      integration: {
        title: "System Integration Services",
        subtitle: "Connect ERP to commerce, sync data in real-time, and automate workflows — the clean, reliable foundation every AI needs to work.",
        paragraphs: [
          "When your systems don't talk to each other, you waste time, money, and sanity. Your team enters data twice, customers receive outdated information, and inventory is never accurate. We build the connections that make all your systems work together seamlessly.",
          "Our experience includes complex integrations between leading ERP systems (Priority, SAP Business One) and commerce platforms (Shopify Plus, WooCommerce), payment and shipping provider connections, multi-source data synchronization into unified systems, and integration with marketing and CRM platforms.",
          "We've worked with leading brands like Skechers Israel, MegaSport, and JD Sports — building integrations that saved thousands of work hours annually and completely eliminated order errors.",
          "The result? End-to-end automated processes, zero duplicate manual work, and clean data synchronized in real-time — exactly the foundation that lets your AI work on reliable information too."
        ],
        features: [
          { title: "ERP to E-commerce", desc: "Connect Priority/SAP to Shopify stores with real-time inventory, orders, customers, and pricing sync." },
          { title: "Payments & Shipping", desc: "Full integration with Tranzila, PayPal, PayMe, and delivery services like HFD, Baldar, and Bpost." },
          { title: "Marketing & CRM", desc: "Connect to Klaviyo, Smile.io, ActiveCampaign, and loyalty programs." },
          { title: "Real-Time Sync", desc: "Bi-directional automatic updates within seconds, with smart retry systems and error handling." },
          { title: "Reporting & Monitoring", desc: "Dashboards for integration status, detailed logs, and issue alerts." },
          { title: "Multi-Source Unification", desc: "Pull data from multiple providers and systems into a single normalized platform." }
        ],
        stack: ["Priority ERP", "SAP Business One", "Shopify Plus API", "WooCommerce", "REST APIs", "Webhooks", "Real-time Sync", "Azure Cloud"],
        useCases: [
          { title: "Sports Retail Chain", desc: "Full Priority-Shopify integration with multi-warehouse inventory sync, automated orders, and dynamic pricing." },
          { title: "International Fashion Brand", desc: "Global ERP connection to local store with multi-currency support and automatic translation." },
          { title: "Product Importer", desc: "Unified catalogs from multiple international suppliers with automatic price and inventory updates." }
        ],
        benefits: [
          "Save 20+ hours of manual work per week",
          "Reduce order errors by 95%",
          "100% accurate inventory across all channels",
          "Response time to changes — seconds instead of hours",
          "Peace of mind with reliable 24/7 system"
        ],
        integrations: ["Priority", "SAP", "Shopify", "WooCommerce", "Tranzila", "PayPal", "Klaviyo", "HFD", "Baldar"],
        caseStudyHint: "See how we built complete integrations for Skechers Israel and MegaSport in our case studies."
      },
      'custom-software': {
        title: "Custom Software Development",
        subtitle: "When off-the-shelf isn't enough, we build the platform, app, or system you actually need — with AI built in where it helps.",
        paragraphs: [
          "Have a unique business need that no existing product solves? That's exactly what we do. We build complex systems from scratch — B2B platforms, marketplaces, mobile apps, and unique information systems.",
          "We've built a B2B platform that aggregates products from 3 different European fashion suppliers into a single system with bulk and dropship order management. We've built a full vegan marketplace with dozens of sellers, real-time delivery management, and automatic vendor payments.",
          "What sets us apart is our proprietary development framework — a collection of battle-tested components, tools, and modules we've built over years of complex projects. Instead of starting from scratch every time, we leverage this foundation to accelerate development significantly. This means faster delivery, lower costs, and proven reliability from day one.",
          "We also bring AI into the build itself — as a tool that accelerates our work, and as a capability we embed into the product when it adds real value: smart search, classification, prediction, and language processing.",
          "We don't believe in generic solutions. Every project starts with deep discovery of your needs, including understanding your business processes, pain points, and goals. Only after we truly understand what you need do we start building.",
          "The result? A system that works exactly the way you need it to, not a system you need to adapt yourself to."
        ],
        features: [
          { title: "B2B Platforms", desc: "Distribution systems, agent portals, complex catalog management, and business customer interfaces." },
          { title: "Marketplaces", desc: "Full online marketplaces with seller management, products, orders, and automatic commissions." },
          { title: "Vendor Portals", desc: "Fulfillment interfaces for order management, packing, and shipping with barcode scanning." },
          { title: "Real-Time Dashboards", desc: "Monitoring and analytics systems with WebSockets for instant updates." },
          { title: "Mobile Applications", desc: "Cross-platform React Native development for iOS and Android apps." },
          { title: "Complex Integrations", desc: "Connections to external providers — FTP, SOAP, REST, CSV, and any other format." }
        ],
        stack: ["React", "React Native", "Angular", "Node.js", "ASP.NET Core", "PostgreSQL", "SQL Server", "Azure Cloud", "SignalR", "CS-Cart"],
        useCases: [
          { title: "B2B Fashion Platform", desc: "Product aggregation from 3 European suppliers, bulk/dropship order management, vendor portal, and Shopify app." },
          { title: "Vegan Marketplace", desc: "Marketplace with dozens of sellers, real-time delivery system, automatic vendor payment calculations." },
          { title: "Distribution Management System", desc: "Platform for multi-warehouse order management with route optimization." },
          { title: "Wholesaler Pricing System", desc: "A full pricing system for a robotic-parts wholesaler that processes thousands of prices." }
        ],
        benefits: [
          "Faster time-to-market with our proven component library",
          "100% customized system for your processes",
          "Competitive advantage with unique technology",
          "Unlimited growth without platform constraints",
          "Full ownership of code and system",
          "Long-term support and partnership"
        ],
        caseStudyHint: "See the platforms we built for Luxhype Fashion and Vegan Place Club in our case studies."
      },
      common: {
        backToHome: "Back to Home",
        professionalOverview: "Professional Overview",
        techCapabilities: "Technology & Capabilities",
        readyToScale: "Ready to scale your business?",
        readyToScaleDesc: "Let's discuss your specific needs and build a custom roadmap for success.",
        bookConsultation: "Book Consultation",
        whyChooseUs: "Why Ronnie Projects?",
        experienceTopBrands: "Experience with Israel's top brands",
        longTermSupport: "Long-term support and partnership",
        viewCaseStudies: "View Case Studies",
        successStoriesTitle: "Success Stories",
        successStoriesDesc: "See how we helped businesses like yours"
      }
    },
    process: {
      title: "Our Proven Development Process",
      subtitle: "From concept to launch, we guide you every step of the way",
      steps: [
        { 
          title: "Discovery Meeting", 
          desc: "We learn your business, goals, and challenges",
          deliverables: ["Deep understanding of needs", "Pain point identification", "Initial direction proposal"],
          clientAction: "Share your vision and business challenges"
        },
        { 
          title: "Analysis & Planning", 
          desc: "Deep dive into requirements and detailed technical specifications",
          deliverables: ["Full specification document", "System architecture", "Detailed proposal"],
          clientAction: "Approve spec and project scope"
        },
        { 
          title: "Design & Development", 
          desc: "Agile development with regular updates and continuous feedback",
          deliverables: ["Demo versions for review", "Testing environment", "Weekly progress updates"],
          clientAction: "Provide feedback and approve milestones"
        },
        { 
          title: "QA & Testing", 
          desc: "Rigorous testing to ensure quality, performance, and security",
          deliverables: ["Complete test reports", "Bug fixes", "Performance optimization"],
          clientAction: "User acceptance testing and final approval"
        },
        { 
          title: "Launch & Support", 
          desc: "Smooth deployment to production with ongoing technical support",
          deliverables: ["Production deployment", "Team training", "Ongoing support"],
          clientAction: "Verify production environment"
        }
      ],
      cta: "Let's Get Started",
      ctaSubtext: "Get free consultation and let's build the perfect solution together",
      badge: "How We Work",
      details: "Details",
      whatYouGet: "What You Get",
      yourPart: "Your Part",
      ctaButton: "Let's Talk",
      stats: {
        projects: "Projects",
        satisfaction: "Satisfaction",
        years: "Years",
        response: "Response"
      }
    },
    whyUs: {
      badge: "Why Us?",
      title: "When Off-The-Shelf Doesn't Cut It",
      desc: "We build what others can't — from complex integrations that plugins don't solve, to smart automation that saves hours of work, to platforms tailored exactly to your business.",
      differentiators: [
        {
          title: "We Solve Hard Problems",
          desc: "When off-the-shelf fails and plugins break, we build what actually works"
        },
        {
          title: "Direct Access to Development Team",
          desc: "You talk directly to the development team, not salespeople or middlemen"
        },
        {
          title: "Diverse Project Experience",
          desc: "From ERP integrations to AI automation to full platform builds"
        },
        {
          title: "End-to-End Solutions",
          desc: "From discovery to deployment to ongoing support, we handle it all"
        }
      ],
      founderName: "Michael Offengenden",
      founderTitle: "Founder & CEO of Ronnie Projects",
      founderQuote: "I love building things that work. I love helping businesses grow. And honestly? I love when clients are happy. That's not marketing — that's just how we operate.",
      cta: "Start Your Project",
      commitmentTitle: "Our Commitment",
      commitmentDesc: "Engineering Excellence",
      uptime: "System Availability"
    },
    contact: {
      title: "Ready to Transform Your Business?",
      subtitle: "Tell us about your project. No obligations, just expert advice.",
      callUs: "Call Us",
      emailUs: "Email Us",
      form: {
        name: "Full Name",
        email: "Email Address",
        phone: "Phone Number",
        company: "Company",
        interest: "How can we help?",
        message: "Tell us about your project",
        submit: "Send Request",
        options: ["AI Agents", "Custom Software Development", "System Integration", "Other"],
        successTitle: "Request Received!",
        successDesc: "Thank you for contacting us. One of our experts will get back to you within one business day.",
        validation: {
          nameRequired: "Please enter your name",
          nameMinLength: "Name must be at least 2 characters",
          nameMaxLength: "Name cannot exceed 100 characters",
          emailRequired: "Please enter your email address",
          emailInvalid: "Please enter a valid email address",
          phoneRequired: "Please enter your phone number",
          phoneInvalid: "Please enter a valid phone number",
          companyMaxLength: "Company name cannot exceed 100 characters",
          interestRequired: "Please select an area of interest",
          messageMaxLength: "Message cannot exceed 2000 characters"
        }
      },
      recommendation: {
        quote: "We love what we do — helping businesses succeed and solving the challenges others say are too complicated. Let's talk.",
        author: "Michael Offengenden",
        position: "Founder & CEO of Ronnie Projects"
      }
    },
    footer: {
      desc: "Software and integration solutions for premium brands. We connect technology to business.",
      copyright: "All rights reserved to Ronnie Projects.",
      servicesTitle: "Services",
      companyTitle: "Company",
      contactTitle: "Contact",
      aiAgents: "AI Agents",
      customSoftware: "Custom Software Development",
      systemIntegration: "System Integration",
      about: "About",
      articles: "Articles",
      caseStudies: "Case Studies",
      resources: "Resources",
      contact: "Contact",
      privacy: "Privacy Policy",
      terms: "Terms of Service",
      accessibility: "Accessibility Statement",
      location: "Modiin, Israel",
      whatsappChat: "Chat on WhatsApp",
      ctaTitle: "Ready to Start?",
      ctaButton: "Start Your Project",
      followLinkedin: "Follow us on LinkedIn (opens in new window)",
      followFacebook: "Follow us on Facebook (opens in new window)",
      followInstagram: "Follow us on Instagram (opens in new window)",
      comingSoon: "coming soon",
      socialLinkComingSoon: "This link will be added soon"
    },
    ai: {
      welcome: "Hello! I'm Ronnie's AI Consultant. Ask me about AI agents, system integration, or custom software development.",
      placeholder: "Ask about your project...",
      online: "Online - Instant Technical Advice"
    },
    logoMarquee: {
      badge: "Partners in Success",
      titlePart1: "Brands That",
      titleHighlight: "Choose Us",
      subtitle: "Proud to work with leading brands in Israel and worldwide",
      ctaQuestion: "Want to join our list of happy clients?",
      ctaLink: "Let's Talk"
    },
    notFound: {
      errorCode: "404",
      title: "Oops! Page Not Found",
      subtitle: "Looks like you've wandered into the digital void",
      description: "The page you're looking for doesn't exist or has been moved. Don't worry — we're here to help you find what you're looking for.",
      backHome: "Back to Home",
      contact: "Contact Us",
      popularPages: "Popular Pages",
      services: "Our Services",
      about: "About Us",
      aiAgents: "AI Agents",
      integration: "System Integration",
      customDev: "Custom Software Development",
      searchHint: "In the meantime, here are some helpful links:"
    },
    testimonials: {
      title: "What Our Clients Say",
      subtitle: "Partnering with leading brands to drive digital transformation.",
      items: [
        {
          quote: "Michael successfully implemented a critical integration between our Priority ERP and Shopify platform, demonstrating both technical prowess and exceptional ability to grasp complex business requirements. The integration has significantly streamlined our operations, resulting in substantial time savings and reduced errors.",
          name: "Roby Bughici",
          title: "E-commerce Manager",
          company: "Skechers Israel",
          image: "https://media.licdn.com/dms/image/v2/D4D03AQG-GVK4N3Bwrg/profile-displayphoto-shrink_100_100/profile-displayphoto-shrink_100_100/0/1672220283922?e=1743638400&v=beta&t=7dhqZ7ypqV6YHAhXfLyN4vt8hwYg-AzUkU9Fo_XAo8M"
        },
        {
          quote: "I have the pleasure of working with Michael and his team on a complex integration project for our e-commerce platform. Michael demonstrated exceptional expertise in both backend and frontend development, seamlessly connecting our Shopify store with our Priority backend system.",
          name: "Hay Dick",
          title: "Digital Manager",
          company: "JD Sports Israel",
          image: "https://media.licdn.com/dms/image/v2/D4D03AQEXf6loAhVFyg/profile-displayphoto-shrink_100_100/profile-displayphoto-shrink_100_100/0/1674560564818?e=1743638400&v=beta&t=5XpRrQiH9hyhPh2MdmT3Iy-1D20p0QVF0d4mA_wB_Mo"
        },
        {
          quote: "I wholeheartedly recommend Ronnie Projects to any business in need of dependable, skilled, and customer-oriented software development services. Their professionalism, technical prowess, and dedication to excellence are truly exceptional.",
          name: "Shmulik Konforty",
          title: "CIO | CTO | Priority ERP Expert",
          company: "One Project",
          image: "https://media.licdn.com/dms/image/v2/C4D03AQGaVNM2SFP9Eg/profile-displayphoto-shrink_100_100/profile-displayphoto-shrink_100_100/0/1652171471036?e=1743638400&v=beta&t=3_E9SZ5TKpSMAvGP6kAP3GWCuH57xqJVxVB9yFLnVFA"
        }
      ]
    },
    logoBar: {
      title: "Our Clients"
    },
    accessibility: {
      title: "Accessibility Menu",
      close: "Close",
      reset: "Reset",
      fontSize: "Font Size",
      increase: "Increase",
      decrease: "Decrease",
      highContrast: "High Contrast",
      highlightLinks: "Highlight Links",
      readableFont: "Readable Font",
      pauseAnimations: "Pause Animations",
      statement: "Accessibility Statement",
      buttonLabel: "Open accessibility menu"
    },
    articlesPage: {
      badge: "Insights",
      title: "Articles",
      subtitle: "Expert perspectives on integration, automation, and custom development for growing businesses.",
      readMore: "Read More",
      noArticles: "No articles available yet. Check back soon!",
      ctaText: "Have a question? Let's talk.",
      ctaButton: "Contact Us"
    },
    seo: {
      siteName: "Ronnie Projects",
      home: {
        title: "Ronnie Projects – AI Agents, Integration & Custom Software",
        description: "We build AI agents, integrate them with your real systems, and develop custom software that off-the-shelf products can't deliver — built to last."
      },
      about: {
        title: "About Us | Ronnie Projects",
        description: "Learn about Ronnie Projects - a software development company with 20+ years of experience in e-commerce integrations and custom development."
      },
      aiAgents: {
        title: "AI Agents & Assistants | Ronnie Projects",
        description: "We build custom AI agents and assistants and connect them to your real systems — ERP, commerce, and live data — so they do real work, reliably."
      },
      integrationServices: {
        title: "System Integration | Ronnie Projects",
        description: "Professional integration services - connecting ERP, CRM, and commerce systems. Priority, SAP, Shopify, and clean data your AI can actually use."
      },
      customDevelopment: {
        title: "Custom Software Development | Ronnie Projects",
        description: "Custom software development - B2B platforms, marketplaces, mobile apps, and complex systems, with AI built in where it helps."
      },
      contact: {
        title: "Contact Us | Ronnie Projects",
        description: "Contact Ronnie Projects - free consultation, quotes and project kickoff. Phone: +972-74-760-2660."
      },
      caseStudies: {
        title: "Case Studies | Ronnie Projects",
        description: "Success stories from Ronnie Projects clients - JD Sports, Skechers, MegaSport and more."
      },
      articles: {
        title: "Articles | Ronnie Projects",
        description: "Expert perspectives on integration, automation, and custom development for growing businesses."
      },
      resources: {
        title: "Resources | Ronnie Projects",
        description: "Insights, guides, and articles to help you make better technology decisions for your business."
      },
      privacy: {
        title: "Privacy Policy | Ronnie Projects",
        description: "Ronnie Projects privacy policy."
      },
      terms: {
        title: "Terms of Service | Ronnie Projects",
        description: "Ronnie Projects terms of service."
      },
      accessibility: {
        title: "Accessibility Statement | Ronnie Projects",
        description: "Ronnie Projects accessibility statement."
      },
      caseStudyDetail: {
        title: "Case Study: {slug} | Ronnie Projects",
        description: "Read the {slug} success story with Ronnie Projects."
      },
      articleDetail: {
        title: "{slug} | Ronnie Projects",
        description: "Read about {slug} on Ronnie Projects."
      },
      resourceDetail: {
        title: "{slug} | Ronnie Projects",
        description: "Read about {slug} on Ronnie Projects."
      },
      notFoundPage: {
        title: "Page Not Found | Ronnie Projects",
        description: "The requested page was not found."
      }
    },
    adminContent: {
      nav: {
        drafts: "Content Drafts",
        topics: "Topics",
        pipelineRunning: "Running pipeline…",
        pipelineSuccess: "Pipeline started — new draft will appear shortly",
        pipelineError: "Pipeline failed",
        dismiss: "Dismiss"
      },
      contentDrafts: {
        title: "Content Drafts",
        runPipeline: "Run Pipeline",
        runPipelineDryRun: "Dry Run",
        emptyState: "No drafts yet. Run the pipeline to generate the first one.",
        tabs: { all: "All", pending: "Pending", approved: "Approved", published: "Published", rejected: "Rejected", failed: "Failed" },
        columns: { title: "Title", topic: "Topic", generated: "Generated", status: "Status", actions: "Actions" },
        status: { pending: "Pending", approved: "Approved", published: "Published", rejected: "Rejected", failed: "Failed" },
        actions: { review: "Review", reject: "Reject" }
      },
      draftReview: {
        backToList: "← Drafts",
        tabs: { enArticle: "EN Article", heArticle: "HE Article", linkedinPost: "LinkedIn Post", seoMeta: "SEO Meta", cardPreview: "Card Preview" },
        actions: { saveEdits: "Save Edits", regenerate: "Re-generate", reject: "Reject", approveWebsiteOnly: "Approve — Website Only", approveAndPublish: "Approve & Publish" },
        publishTarget: { label: "Publish to", articles: "Articles", resources: "Resources" },
        rejectModal: { title: "Reject Draft", placeholder: "Reason for rejection…", confirm: "Reject", cancel: "Cancel" },
        seoScore: { title: "SEO Score", wordCount: "Word count 900–1200", keywordInH1: "Keyword in H1", keywordInFirst100: "Keyword in first 100 words", metaLength: "Meta description length", internalLinks: "Internal links present" },
        metadata: { topic: "Topic", keywords: "Keywords", sources: "Sources", estimatedCost: "Est. cost", generatedAt: "Generated" },
        autoSaving: "Saving…",
        saved: "Saved",
        charCount: "chars",
        linkedinPanel: {
          title: "LinkedIn",
          notScheduled: "Not scheduled",
          scheduleBtn: "Schedule for LinkedIn",
          postNowBtn: "Post Now",
          changeBtn: "Change",
          cancelBtn: "Cancel Post",
          scheduledFor: "Scheduled for",
          publishedAt: "Published on",
          failedWith: "Failed:",
          confirmTitle: "Pick date & time",
          confirmSave: "Save",
          confirmCancel: "Cancel",
        },
      },
      contentTopics: {
        title: "Content Topics",
        createNew: "New Topic",
        emptyState: "No topics yet. Create one to start the pipeline.",
        columns: { title: "Title (EN)", angle: "Angle", priority: "Priority", status: "Status", actions: "Actions" },
        status: { active: "Active", inProgress: "In Progress", done: "Done", paused: "Paused" },
        angles: { educational: "Educational", caseStudy: "Case Study", thoughtLeadership: "Thought Leadership", howTo: "How-To", newsReaction: "News Reaction" },
        languageNames: { he: "Hebrew", fr: "French", ar: "Arabic", de: "German", es: "Spanish", it: "Italian", pt: "Portuguese", zh: "Chinese", ja: "Japanese", ru: "Russian", tr: "Turkish" },
        form: { titleEn: "Title (EN)", keywordsEn: "Keywords EN (comma-separated)", translations: "Translations", translationsAdd: "Add Language", translationsLang: "Language", translationsTitle: "Title (optional)", translationsKeywords: "Keywords (comma-separated, optional)", translationsRemove: "Remove", angle: "Content Angle", audience: "Target Audience", priority: "Priority (1=high, 5=low)", notes: "Notes", isRecurring: "Recurring topic", status: "Status", save: "Save", cancel: "Cancel" },
        actions: { edit: "Edit", delete: "Delete" },
        deleteConfirm: "Delete this topic?"
      },
      helpTips: {
        topics: {
          page: "Topics are the content ideas that feed the AI pipeline. The pipeline picks the highest-priority Active topic and generates a full bilingual article + LinkedIn post.",
          angle: "The editorial lens for the article.\n• Educational — explain a concept\n• Case Study — tell a client story\n• Thought Leadership — share an opinion\n• How-To — step-by-step guide\n• News Reaction — respond to an industry event",
          priority: "1 = pick first, 5 = pick last. The pipeline always selects the lowest-numbered Active topic that hasn't been used recently.",
          status: "Only Active topics are eligible to be picked up by the pipeline. Set to Paused to skip a topic temporarily, or Done once it's been published and retired.",
          isRecurring: "When checked, the topic returns to Active after its draft is published, so it can generate fresh content on the same theme again in the future.",
          keywordsEn: "Comma-separated English keywords. The AI uses these to optimise titles, headings, and meta tags for search engines.",
          translations: "Add translations for each language you want to publish in. Each entry is optional — leave title or keywords blank to let the AI generate them from the English version.",
          audience: "Describe who this article is for — e.g. 'CTOs at mid-size retail brands'. The AI tailors its tone, examples, and level of technical detail accordingly.",
          notes: "Internal notes for your team only. These are not sent to the AI and do not appear in the generated article.",
        },
        drafts: {
          page: "Drafts are AI-generated articles waiting for your review. The pipeline runs Gemini (research + outline) then Claude (write + polish) to produce a bilingual article, LinkedIn post, and SEO metadata.",
          runPipeline: "Fetches trending industry sources, picks the top Active topic, and generates a full bilingual article using Gemini + Claude. A new draft will appear in the Pending tab within a minute.",
          dryRun: "Simulates the full pipeline without saving anything to the database. Use this to verify that sources are reachable and settings are correct before a real run.",
        },
        draftReview: {
          regenerate: "Re-runs Claude Phases 2 and 3 using the stored Gemini outline. Useful if you want a fresh take on the writing without re-researching sources. The previous text will be overwritten.",
          reject: "Rejects this draft and automatically returns the topic to the Active queue so the pipeline can pick it up again.",
          tabEn: "The full English article in Markdown format. Use # for the H1 title and ## for section headings. Changes are saved automatically.",
          tabHe: "The full Hebrew article in Markdown format, written right-to-left. Changes are saved automatically.",
          tabLinkedin: "A ready-to-post LinkedIn update. Aim for under 1,300 characters for best reach — the character counter below helps.",
          tabSeoMeta: "Meta title (keep under 70 chars) and meta description (150–160 chars). These appear in Google search results and social sharing previews.",
          tabCardPreview: "The case-study card shown on listing pages — brand, industry, headline, summary, tags, and color. AI fills these on generation; edit per language and click 'Regenerate Card' for a fresh AI pass.",
          seoScore: "A quick checklist of the five most important on-page SEO signals. Aim for 4/5 or higher before approving.",
        },
      },
      settings: {
        title: "Settings",
        languages: {
          title: "Site Languages",
          description: "Manage which languages the site and content pipeline support. English is the default and cannot be removed.",
          code: "Code",
          nameEn: "Name (EN)",
          nameNative: "Native name",
          isRtl: "RTL",
          isActive: "Active",
          defaultBadge: "Default",
          addTitle: "Add Language",
          codePlaceholder: "e.g. fr",
          nameEnPlaceholder: "e.g. French",
          nameNativePlaceholder: "e.g. Français",
          save: "Save",
          cancel: "Cancel",
          deleteConfirm: "Delete this language?",
          cannotDeleteDefault: "Cannot delete the default language",
        },
      },
      linkedinSchedule: {
        title: "LinkedIn Schedule",
        connection: { connected: "Connected", partial: "Partial — missing Author URN", notConnected: "Not Connected", connectBtn: "Connect LinkedIn" },
        scheduleNewBtn: "Schedule New Post",
        monthly: "Monthly",
        weekly: "Weekly",
        status: { not_scheduled: "Not scheduled", scheduled: "Scheduled", published: "Published", failed: "Failed", pending: "Not scheduled" },
        modal: { title: "Schedule LinkedIn Post", selectDraft: "Select draft", dateTime: "Date & time", save: "Schedule", cancel: "Cancel", noDrafts: "No approved drafts available to schedule." },
        editModal: { title: "Edit Schedule", newTime: "New date & time", save: "Save", cancelPost: "Cancel Post", close: "Close" },
        postNow: "Post Now",
        scheduledFor: "Scheduled for",
        publishedAt: "Published",
        noPostsInPeriod: "No posts scheduled for this period.",
        daysShort: ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"],
        months: ["January", "February", "March", "April", "May", "June", "July", "August", "September", "October", "November", "December"],
      },
      seoIntelligence: {
        title: "SEO Intelligence",
        refresh: "Refresh",
        generating: "Generating…",
        lastRefresh: "Last refresh:",
        never: "Never",
        days7: "7 days",
        days28: "28 days",
        days90: "90 days",
        keywords: {
          title: "Keyword Performance",
          query: "Query",
          page: "Page",
          clicks: "Clicks",
          impressions: "Impr.",
          ctr: "CTR",
          position: "Pos.",
          createArticle: "Create article",
        },
        pages: {
          title: "Top Pages",
          page: "Page",
          sessions: "Sessions",
          bounceRate: "Bounce %",
          avgDuration: "Avg Duration",
          declining: "Declining",
        },
        gaps: {
          title: "Content Gaps",
          description: "Keywords with >50 impressions and position >10 that have no matching published page.",
          empty: "No content gaps detected.",
        },
        recommendations: {
          title: "AI Recommendations",
          generate: "Generate Now",
          generatedAt: "Generated:",
          addToTopics: "Add to Topics",
          empty: "No recommendations yet. Click \"Generate Now\" to run the analysis.",
          priority: { high: "High", medium: "Medium", low: "Low" },
        },
        errors: {
          keywordsFailed: "Could not load keyword data. Check GOOGLE_SEARCH_CONSOLE_SITE_URL and service account permissions.",
          pagesFailed: "Could not load page data. Check GOOGLE_ANALYTICS_PROPERTY_ID and service account permissions.",
          notConfigured: "Google credentials not configured.",
        },
      },
      prompts: {
        title: "Language Prompts",
        intro: {
          title: "Per-language polish & translation style guides",
          description: "Whatever you save here is used as a style guide during the polish/translate step, every time an article is produced in that language. When no custom prompt is set, the built-in default is used.",
        },
        empty: "No active languages. Add one in Settings first.",
        notSet: "No custom prompt set yet.",
        lastUpdated: "Last updated {date}",
        chars: "chars",
        placeholder: "Write the style guide for this language here…",
        confirmDelete: "Delete the custom prompt for {lang}? The system will fall back to the built-in default.",
        direction: { label: "Direction:", ltr: "LTR", rtl: "RTL" },
        badges: { custom: "Custom", default: "Default" },
        actions: { save: "Save", reset: "Reset", delete: "Delete" },
        toasts: { saved: "{lang} prompt saved", deleted: "{lang} prompt deleted" },
        errors: {
          loadFailed: "Failed to load prompts.",
          saveFailed: "Failed to save prompt.",
          deleteFailed: "Failed to delete prompt.",
          emptyPrompt: "Prompt cannot be empty.",
        },
      },
    }
  }
};
